import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react"
import { E2E_MODE } from "../lib/env"

const SESSION_KEY = "admin_password"
// E2E mode uses a default password for automated testing
// Must match ADMIN_PASSWORD set in scripts/e2e-local-convex.sh
const E2E_DEFAULT_PASSWORD = "test-password"

interface AdminAuthContextValue {
	password: string | null
	isAuthenticated: boolean
	isChecking: boolean
	setPassword: (pwd: string) => void
	clearPassword: () => void
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

/**
 * Provider for admin auth state.
 * Wrap the app with this to share auth state across components.
 */
export function AdminAuthProvider({ children }: { children: ReactNode }) {
	const [password, setPasswordState] = useState<string | null>(null)
	const [isChecking, setIsChecking] = useState(true)

	// Load password from session storage on mount (or use default in E2E mode)
	useEffect(() => {
		if (E2E_MODE) {
			// In E2E mode, use a default password for automated testing
			setPasswordState(E2E_DEFAULT_PASSWORD)
			setIsChecking(false)
			return
		}
		const stored = sessionStorage.getItem(SESSION_KEY)
		setPasswordState(stored)
		setIsChecking(false)
	}, [])

	const setPassword = useCallback((pwd: string) => {
		sessionStorage.setItem(SESSION_KEY, pwd)
		setPasswordState(pwd)
	}, [])

	const clearPassword = useCallback(() => {
		sessionStorage.removeItem(SESSION_KEY)
		setPasswordState(null)
	}, [])

	return (
		<AdminAuthContext.Provider
			value={{
				password,
				isAuthenticated: !!password,
				isChecking,
				setPassword,
				clearPassword,
			}}
		>
			{children}
		</AdminAuthContext.Provider>
	)
}

/**
 * Hook to access admin auth state.
 * Must be used within AdminAuthProvider.
 */
export function useAdminAuth(): AdminAuthContextValue {
	const context = useContext(AdminAuthContext)
	if (!context) {
		throw new Error("useAdminAuth must be used within AdminAuthProvider")
	}
	return context
}
